import { PreviewSurgery } from '~/components/SurgeryRows'
import { getFlag } from '~/hooks/useFlag'
import { Include, Surgery, SurgeryMetadataSuggestion } from '~/storm'
import { getBookingIdOrId, isScheduledSurgery } from '~/utils/dips'
import { getSurgeryMetadata } from '~/utils/surgeryMetadata'

import { getIsNew } from '../../utils'
import { hasNewSuggestions } from './analysisTagsUtils'
import { normalizePlanningNoteAnalysisSuggestions } from './normalizePlanningNoteAnalysisSuggestions'

type SurgeryWithMetadataSuggestions = Include<Surgery, { surgeryMetadataSuggestions: true }>

function isSurgeryWithMetadataSuggestions(surgery: Surgery): surgery is SurgeryWithMetadataSuggestions {
    return 'surgeryMetadataSuggestions' in surgery
}

function getMetadataSuggestions(surgery: Surgery): SurgeryMetadataSuggestion | undefined {
    return isSurgeryWithMetadataSuggestions(surgery) ? surgery.surgeryMetadataSuggestions : undefined
}

type Args = {
    surgery: Surgery
    commentText: string | null | undefined
}

export function getSuggestionVisibility({ surgery, commentText }: Args) {
    const isStaticMetadataSuggestionOn = getFlag('planning_note__calculate_suggestions')
    const surgeryMetadata = getSurgeryMetadata(surgery)
    const metadataSuggestions = getMetadataSuggestions(surgery)
    const normalizedSuggestions = normalizePlanningNoteAnalysisSuggestions(metadataSuggestions)
    const hasNewSuggestionValues = normalizedSuggestions ? hasNewSuggestions({ suggestions: normalizedSuggestions, surgery }) : false

    const hasCommentChangedInDIPS = surgeryMetadata?.comment !== commentText
    const areSuggestionsClosed = surgeryMetadata?.reified_suggestions === true && !hasCommentChangedInDIPS
    const shouldShowSuggestions = !areSuggestionsClosed

    let needsReview = false

    if (isStaticMetadataSuggestionOn) {
        needsReview =
            (shouldShowSuggestions && metadataSuggestions?.needs_review === true && hasNewSuggestionValues && hasCommentChangedInDIPS) || getIsNew(surgery)
    } else {
        needsReview = hasCommentChangedInDIPS || getIsNew(surgery)
    }

    return {
        hasCommentChangedInDIPS,
        shouldShowSuggestions,
        needsReview,
    }
}

export const shouldShowConfirmationStatus = (surgery: Surgery, previewSurgeries: PreviewSurgery[]): boolean => {
    const isScheduledItem = isScheduledSurgery(surgery)
    if (isScheduledItem) return true
    const isPreviewPatient = previewSurgeries.some(previewSurgery => previewSurgery.surgery.bookingId === getBookingIdOrId(surgery))
    return isPreviewPatient
}
